import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

import { FormField } from "@/components/ui/FormField";
import {
    createReferral,
    updateReferral,
} from "@/services/offers";

interface ReferralModalProps {
    referral?: any;
    onClose: () => void;
    onSuccess: () => void;
}

interface ReferralFormData {
    Image: FileList;
    Amount: string;
}

export default function ReferralModal({
    referral,
    onClose,
    onSuccess,
}: ReferralModalProps) {
    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting, errors },
    } = useForm<ReferralFormData>({
        defaultValues: {
            Amount: "",
        },
    });
    const [previewImage, setPreviewImage] = useState("");
    const [selectedFileName, setSelectedFileName] = useState("");

    useEffect(() => {
        if (referral) {
            reset({
                Amount: referral.Amount || "",
            });
        }
    }, [referral, reset]);

    useEffect(() => {
        if (referral?.Image) {
            setSelectedFileName(
                referral.Image.split("/").pop() || ""
            );
        }
    }, [referral]);

    useEffect(() => {
        if (referral) {
            reset({
                Amount: referral.Amount || "",
            });
            const imageBaseUrl =
                import.meta.env.VITE_BASE_URL.replace(
                    "/api",
                    ""
                );

            setPreviewImage(
                referral.Image
                    ? `${imageBaseUrl}${referral.Image}`
                    : ""
            );
        }
    }, [referral, reset]);

    const onSubmit = async (
        values: ReferralFormData
    ) => {
        try {
            if (!values.Amount) {
                toast.error("Amount is required");
                return;
            }

            const formData = new FormData();

            formData.append("Amount", values.Amount);
            formData.append("Status", "true")
            if (values.Image?.[0]) {
                formData.append(
                    "Image",
                    values.Image[0]
                );
            }

            let response;

            if (referral) {
                response = await updateReferral(
                    referral.id,
                    formData
                );
            } else {
                response = await createReferral(formData);
            }

            if (
                response.status === 200 ||
                response.status === 201
            ) {
                toast.success(
                    referral
                        ? "Referral updated successfully"
                        : "Referral created successfully"
                );

                onSuccess();
            }
        } catch (error) {
            toast.error("Something went wrong");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
                {/* Header */}
                <div
                    className="flex items-center justify-between px-6 py-5"
                    style={{
                        background:
                            "linear-gradient(135deg, #2d3440 0%, #535c6b 100%)",
                    }}
                >
                    <h2 className="text-2xl font-bold text-white">
                        {referral
                            ? "Edit Referral"
                            : "Create Referral"}
                    </h2>

                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white/10 p-2 rounded-lg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <FormField
                            control={control}
                            name="Amount"
                            label="Amount"
                            type="text"
                            placeholder="Enter amount"
                            required
                            error={errors.Amount?.message}
                        />

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">
                                Referral Image
                            </label>

                            <div
                                className={`w-full border rounded-xl px-3 py-2 flex items-center justify-between ${errors.Image
                                        ? "border-red-500"
                                        : "border-gray-300"
                                    }`}
                            >
                                <span className="text-sm text-gray-600 truncate">
                                    {selectedFileName || "No file selected"}
                                </span>

                                <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-lg text-sm">
                                    Browse
                                    <input
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        {...register("Image", {
                                            onChange: (e) => {
                                                const file =
                                                    e.target.files?.[0];

                                                if (file) {
                                                    setSelectedFileName(
                                                        file.name
                                                    );

                                                    setPreviewImage(
                                                        URL.createObjectURL(file)
                                                    );
                                                }
                                            },
                                        })}
                                    />
                                </label>
                            </div>
                        </div>

                        {previewImage ? (
                            <div className="md:col-span-2">
                                <img
                                    src={previewImage}
                                    alt="Referral"
                                    className="w-40 h-40 object-cover rounded-2xl border"
                                    onError={(e) => {
                                        e.currentTarget.src =
                                            "https://via.placeholder.com/150?text=No+Image";
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="md:col-span-2 w-40 h-40 border rounded-2xl flex items-center justify-center text-gray-500">
                                No Image Available
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex gap-3 border-t p-5">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold"
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 py-3 rounded-xl bg-mlb-gold hover:bg-mlb-gold/90 text-brand-700 font-semibold"
                        >
                            {isSubmitting
                                ? "Saving..."
                                : referral
                                    ? "Edit Referral"
                                    : "Create Referral"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}